/**
 * @file SendPOEmailModal.js
 * @version 2.8.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @description Modal for sending purchase order to supplier via email with contact validation
 * @created 2025-12-15
 * @modified 2026-10-21
 *
 * 2.6.0 (2026-10-08, Cam): "Show quantities as" (Tonnes first / Packs first)
 * beside the attachment, so it can be switched before sending: saved to the PO
 * at once (PATCH /api/purchase-orders/:id/qty-display, audited) and the
 * preview redrawn. Allowed on any live PO — it is presentation only.
 *
 * 2.7.0 (2026-10-21, Cam): the message carries the collection — a PO-H's
 * "Please collect from <supplier>, <address> on <date>, quoting PO-D…", a PO-D
 * on Third-Party Collection's "Collected by <haulier> (PO-H…)" — from
 * contact-info's collectionNote; a collection point with no address held is
 * warned about before sending (collectionWarnings).
 *
 * 2.8.0 (2026-10-21, Cam — "The option to show packs or tonnes first is
 * confusing"): the switch is gone. A line counted in packs prints its packs
 * first, a tonne line its tonnes (the server's utils/poQtyDisplay).
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './SendPOEmailModal.css';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import SenderSelect from './SenderSelect';
import TickCheckbox from './TickCheckbox';
import PdfCanvasPreview from './PdfCanvasPreview';

/*
 * Subject and message for this PO. A PO-H is a haulage order and says so
 * (the modal used to call every one a Purchase Order). On a third-party
 * hauled order the customer is named in the subject, so the haulier, the
 * mill and the store can each tell which delivery it is from the inbox.
 */
const docLabelFor = (number, poType) =>
  (poType === 'haulage' || /^PO-H/i.test(number || '') ? 'Haulage Order' : 'Purchase Order');
/*
 * A RE-send says so. `revision` is { summary, changes[] } — handed in by the
 * screen that changed the PO (an order edit, a date moved on a planning board)
 * — and the subject leads with "Revised" and names the change, and the message
 * lists what changed, so the supplier does not have to diff two PDFs.
 */
/*
 * A CANCELLATION says so, and says it first. `cancellation` is { reason } —
 * handed in by the screen archiving the PO (an archived order, a PO emptied by
 * an edit, a haulier replaced) — and the attached PDF is the same order
 * bannered CANCELLED, so the supplier can match it to the one they hold.
 */
const textFor = (number, poType = null, customer = null, revision = null, cancellation = null, note = null) => {
  const label = docLabelFor(number, poType);
  // Where to collect / who collects (services/haulageCollectionService), after the first paragraph.
  const withNote = (first) => (note ? `${first}\n\n${note}` : first);
  if (cancellation) {
    return {
      subject: `CANCELLED: ${label} ${number}${customer ? ` — ${customer}` : ''}`,
      body: `Please cancel ${label.toLowerCase()} ${number}. We no longer need it`
        + (cancellation.reason ? ` (${cancellation.reason})` : '')
        + `, so please do not ${label === 'Haulage Order' ? 'collect or deliver' : 'supply'} against it.`
        + `\n\nA copy marked CANCELLED is attached for your records. Please reply to confirm you have cancelled it.`
    };
  }
  if (revision) {
    const changes = (revision.changes || []).filter(Boolean);
    return {
      subject: `Revised ${label} ${number}${customer ? ` — ${customer}` : ''}${revision.summary ? ` — ${revision.summary}` : ''}`,
      body: withNote(`Please find attached the revised ${label.toLowerCase()} ${number}, which replaces the one we sent before.`)
        + (changes.length ? `\n\nWhat has changed:\n${changes.map(c => `• ${c}`).join('\n')}` : '')
        + `\n\nPlease confirm acceptance of the revised order at your earliest convenience.`
    };
  }
  return {
    subject: `${label} ${number}${customer ? ` — ${customer}` : ''}`,
    body: `${withNote(`Please find attached ${label.toLowerCase()} ${number}.`)}\n\nPlease confirm acceptance at your earliest convenience.`
  };
};

const defaultsFor = (number, fallbackTo, revision = null, cancellation = null) => ({ to: fallbackTo, ...textFor(number, null, null, revision, cancellation) });

function SendPOEmailModal({ purchaseOrder, user, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  /*
   * Every address this PO could go to, each with its own tick. A supplier is
   * rarely one mailbox — there is the account's address and then the people on
   * the contact record — and which of them normally gets orders is remembered
   * per person (include_in_po_emails), so this opens on the right answer
   * instead of making somebody retype it each time.
   */
  const [recipients, setRecipients] = useState([]);
  // The earlier send the ticks were taken from — see contact-info on the server.
  const [rememberedFrom, setRememberedFrom] = useState(null);

  // Email destination: 'contact' or 'other'
  const [destination, setDestination] = useState('contact');

  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  // "Send as" token resolved server-side (see SenderSelect). Purchase orders
  // and haulage orders go from admin@ by default — the shared mailbox the
  // supplier's reply and confirmation should land in, not one person's inbox.
  // The picker still offers the user's own address.
  const [sendAs, setSendAs] = useState('admin');
  // A PO-H collection point with no address held (contact-info).
  const [collectionWarnings, setCollectionWarnings] = useState([]);

  useSlideOutButton();

  /*
   * The PO this modal is for, as three primitives rather than the object it
   * arrived in.
   *
   * THIS IS WHY: everything below is seeded once, from an effect. Keying that
   * effect on the `purchaseOrder` OBJECT made it fire again every time a caller
   * handed over a freshly-built one — and SendToSupplierFlow built one on every
   * render, so any re-render of the screen underneath (the dashboard re-renders
   * on its 20-second status poll, whether or not anyone has touched anything)
   * silently re-fetched the contact, overwrote the subject and message someone
   * was halfway through typing, and reloaded the PDF preview. Comparing the id
   * and number instead means the effect runs once per actual purchase order and
   * no caller can cause it again by accident.
   */
  const poId = purchaseOrder?.purchaseOrderID || purchaseOrder?.dbId || null;
  const poNumber = purchaseOrder?.purchaseOrderNumber || '';
  const poSupplierEmail = purchaseOrder?.supplier_email || '';
  // A revision is read once, with the PO it came with (see textFor).
  const revisionRef = React.useRef(purchaseOrder?.revision || null);
  // Likewise a cancellation — which also changes the title, button and PDF.
  const cancellationRef = React.useRef(purchaseOrder?.cancellation || null);
  const isCancellation = !!cancellationRef.current;



  // Fetch contact info + set up defaults
  useEffect(() => {
    if (!poId) {
      /*
       * Nothing to ask the server about. Bailing out silently left both panes
       * spinning for ever, which reads as "still loading" and never resolves —
       * worse than the 404 it replaced, because there is nothing to act on.
       * Seed what we do have and let the preview report its own failure.
       */
      setEmailData(defaultsFor(poNumber, poSupplierEmail, revisionRef.current, cancellationRef.current));
      setLoading(false);
      setLoadingPdf(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchase-orders/${poId}/contact-info`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setContactInfo(data.contact);
          setRecipients(Array.isArray(data.recipients) ? data.recipients : []);
          setRememberedFrom(data.rememberedFrom || null);
          setCollectionWarnings(Array.isArray(data.collectionWarnings) ? data.collectionWarnings : []);

          setEmailData({
            to: data.contact.email || poSupplierEmail,
            ...textFor(poNumber, data.poType, data.thirdPartyCustomer, revisionRef.current, cancellationRef.current, data.collectionNote || null)
          });
        } else {
          // Fallback to purchaseOrder data
          setEmailData({ to: poSupplierEmail, ...textFor(poNumber, null, null, revisionRef.current, cancellationRef.current) });
        }
      } catch {
        // Fallback
        setEmailData({ to: poSupplierEmail, ...textFor(poNumber, null, null, revisionRef.current, cancellationRef.current) });
      } finally {
        setLoading(false);
      }
    })();

    loadPdfPreview(poId);
  }, [poId, poNumber, poSupplierEmail]);

  const loadPdfPreview = async (poId) => {
    setLoadingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-orders/${poId}/pdf${cancellationRef.current ? '?cancelled=true' : ''}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to generate PDF preview');
      const blob = await response.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('PDF preview error:', err);
    } finally {
      setLoadingPdf(false);
    }
  };

  // Switch destination
  useEffect(() => {
    if (loading) return;
    if (destination === 'contact') {
      const ticked = recipients.filter(r => r.checked).map(r => r.email);
      setEmailData(prev => ({
        ...prev,
        to: ticked.length
          ? ticked.join(', ')
          : (contactInfo?.email || poSupplierEmail)
      }));
    } else {
      setEmailData(prev => ({ ...prev, to: user?.email || '' }));
    }
  }, [destination, contactInfo, user, loading, poSupplierEmail, recipients]);

  const toggleRecipient = (email) => {
    setRecipients(prev => prev.map(r => (r.email === email ? { ...r, checked: !r.checked } : r)));
  };

  // Ticks that are not the contact record's own — from the last send, or
  // changed here. Said, with the way back, so nobody sends to the wrong depot
  // believing it was the supplier's usual address.
  const differsFromDefault = recipients.some(r => !!r.checked !== !!r.default);
  const resetRecipients = () => {
    setRecipients(prev => prev.map(r => ({ ...r, checked: !!r.default })));
  };

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!emailData.to) {
      setError('Recipient email address is required');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/purchase-orders/${poId}/send-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...emailData, sendAs, ...(isCancellation ? { cancellation: true } : {}) })
        }
      );

      const data = await response.json();

      if (data.success) {
        if (onSent) onSent(data.message || 'Email sent successfully');
        onClose();
      } else {
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  if (!purchaseOrder) return null;

  // Contact validation warnings
  const isCustomerOnly = contactInfo && contactInfo.contactType === 'CUSTOMER';
  const isSendingToNonContact = destination === 'other';

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content send-po-email-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-symbols-rounded">{isCancellation ? 'cancel_schedule_send' : 'mail'}</span>
            {isCancellation ? `Send Cancellation — ${poNumber}` : 'Send Purchase Order'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <div className="modal-body">
          <div className="email-form">
            {loading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Contact info summary */}
                {contactInfo && (
                  <div className="contact-validation-box">
                    <div className="contact-summary">
                      <span className="cv-label">Contact:</span>
                      <span className="cv-value">{contactInfo.companyName}</span>
                    </div>
                    <div className="contact-summary">
                      <span className="cv-label">Account:</span>
                      <span className="cv-value">{contactInfo.accountNo}</span>
                    </div>
                    <div className="contact-summary">
                      <span className="cv-label">Type:</span>
                      <span className={`cv-value cv-type cv-type-${(contactInfo.contactType || '').toLowerCase()}`}>
                        {contactInfo.contactType || 'Unknown'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Customer warning for POs */}
                {isCustomerOnly && (
                  <div className="contact-warning warning-customer">
                    <span className="material-symbols-rounded">warning</span>
                    <span>This contact is a <strong>customer</strong>, not a supplier. Are you sure you want to send this purchase order to them?</span>
                  </div>
                )}

                {/* A PO-H collection point the haulier is not told the address of */}
                {!isCancellation && collectionWarnings.map(w => (
                  <div key={w} className="contact-warning warning-customer">
                    <span className="material-symbols-rounded">warning</span>
                    <span>{w}</span>
                  </div>
                ))}

                {/* Destination toggle */}
                <div className="form-group">
                  <label>Send To</label>
                  <div className="destination-toggle">
                    <button
                      className={`dest-btn ${destination === 'contact' ? 'active' : ''}`}
                      onClick={() => setDestination('contact')}
                      type="button"
                    >
                      <span className="material-symbols-rounded">business</span>
                      Contact on PO
                    </button>
                    <button
                      className={`dest-btn ${destination === 'other' ? 'active' : ''}`}
                      onClick={() => setDestination('other')}
                      type="button"
                    >
                      <span className="material-symbols-rounded">alternate_email</span>
                      Other Email
                    </button>
                  </div>
                </div>

                {/* Warning for non-contact send */}
                {isSendingToNonContact && (
                  <div className="contact-warning warning-other">
                    <span className="material-symbols-rounded">info</span>
                    <span>You are emailing this purchase order to an address that is <strong>not</strong> the contact on the PO.</span>
                  </div>
                )}

                {/* Who it goes to. The supplier's own address plus everyone on
                    its contact record, each with a tick — the ones already
                    marked as receiving purchase orders open ticked, so the
                    usual case needs no clicks. Ticking here rewrites the To
                    field below, which stays visible: it is what will actually
                    be sent, and it is still the place to add somebody who is
                    not on the record. */}
                {destination === 'contact' && recipients.length > 0 && (
                  <div className="form-group po-recipients">
                    <label>Send to</label>
                    <div className="po-recipient-list">
                      {recipients.map((r) => (
                        <label key={r.email} className="po-recipient">
                          <TickCheckbox
                            id={`po-recipient-${r.email}`}
                            checked={!!r.checked}
                            onChange={() => toggleRecipient(r.email)}
                            size={15}
                            ariaLabel={`Send to ${r.email}`}
                          />
                          <span className="po-recipient-who">
                            {r.name && <span className="po-recipient-name">{r.name}</span>}
                            <span className="po-recipient-email">{r.email}</span>
                          </span>
                          {r.source === 'account' && <span className="po-recipient-tag">Account</span>}
                          {/* Stock collected by a third-party haulier: the store
                              gets a copy of the PO-H — it has no PO-D of its own. */}
                          {r.source === 'store' && <span className="po-recipient-tag" title="Collected from our stock — the store gets a copy">Store copy</span>}
                        </label>
                      ))}
                    </div>
                    {differsFromDefault && (
                      <div className="po-recipients-differs">
                        <span className="material-symbols-rounded">history</span>
                        <span>
                          {rememberedFrom
                            ? <>As last sent ({rememberedFrom.purchaseOrderNumber}, {new Date(rememberedFrom.sentAt).toLocaleDateString('en-GB')}) — not the supplier's default recipients.</>
                            : <>Not the supplier's default recipients.</>}
                        </span>
                        <button type="button" className="po-recipients-reset" onClick={resetRecipients}>
                          <span className="material-symbols-rounded">restart_alt</span>
                          Reset to default
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>To</label>
                  <input
                    type="text"
                    value={emailData.to}
                    onChange={e => handleInputChange('to', e.target.value)}
                    placeholder="supplier@example.com"
                    className={!emailData.to ? 'error' : ''}
                  />
                  {destination === 'contact' && !emailData.to && (
                    <span className="field-hint error">
                      <span className="material-symbols-rounded">warning</span>
                      No email address found for this supplier — tick someone above, or type an address
                    </span>
                  )}
                  {emailData.to.includes(',') && (
                    <span className="field-hint">
                      {emailData.to.split(',').filter(a => a.trim()).length} recipients
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>From</label>
                  <SenderSelect user={user} value={sendAs} onChange={setSendAs} disabled={sending} />
                </div>

                <div className="form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={e => handleInputChange('subject', e.target.value)}
                    placeholder="Purchase Order..."
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={emailData.body}
                    onChange={e => handleInputChange('body', e.target.value)}
                    rows={8}
                    placeholder="Enter your message..."
                  />
                </div>


                <div className="form-group attachment-group">
                  <label>Attachment</label>
                  <div className="attachment-info">
                    <span className="material-symbols-rounded">picture_as_pdf</span>
                    <span>{poNumber}{isCancellation ? ' CANCELLED' : ''}.pdf</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="pdf-preview">
            <label>PDF Preview</label>
            <div className="pdf-container">
              {loadingPdf ? (
                <LoadingSpinner />
              ) : pdfUrl ? (
                <PdfCanvasPreview
                  className="pdf-canvas-fill "
                  loadKey={pdfUrl}
                  load={() => fetch(pdfUrl, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error('Could not load the PDF'); return r.arrayBuffer(); })}
                />
              ) : (
                <div className="pdf-error">
                  <span className="material-symbols-rounded">error</span>
                  <span>Could not load PDF preview</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="material-symbols-rounded">error</span>
            {error}
          </div>
        )}

        <div className="modal-footer">
          <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={sending}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
          <button
            className="btn-slideout btn-slideout-primary"
            onClick={handleSend}
            disabled={sending || !emailData.to || loading}
          >
            <div className="icon-wrapper">
              <span className={`material-symbols-rounded ${sending ? 'spinning' : ''}`}>
                {sending ? 'sync' : 'send'}
              </span>
            </div>
            <span className="btn-label">{sending ? 'Sending...' : isCancellation ? 'Send Cancellation' : 'Send Email'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SendPOEmailModal;
